import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Expand, GalleryHorizontal, Maximize2, Minus, Plus, X } from 'lucide-react';
import { imageFitScale, imagePlacement, imageZoomScroll, initialImageFit, type ImageFit, type ImageScroll, type ImageSize } from './image-lightbox-geometry';
import './image-lightbox.css';

export interface LightboxImage { id: string; source: string; title?: string }
export interface ImageLightboxProps {
  images: LightboxImage[];
  initialIndex?: number;
  onClose: () => void;
  onImageError?: (image: LightboxImage) => Promise<string | null>;
}
type ImageView = { mode: ImageFit; scale: number };

/** Only the trusted parent supplies resolved image sources; this component never resolves paths. */
export function ImageLightbox({ images, initialIndex = 0, onClose, onImageError }: ImageLightboxProps) {
  const [index, setIndex] = useState(Math.max(0, Math.min(initialIndex, images.length - 1)));
  const [thumbnails, setThumbnails] = useState(false);
  const [viewport, setViewport] = useState<ImageSize>({ width: 1, height: 1 });
  const [dimensions, setDimensions] = useState<Record<string, ImageSize>>({});
  const [views, setViews] = useState<Record<string, ImageView>>({});
  const [sources, setSources] = useState<Record<string, string>>({});
  const [failures, setFailures] = useState<Record<string, boolean>>({});
  const [loaded, setLoaded] = useState<Record<string, boolean>>({});
  const [dragging, setDragging] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const thumbStrip = useRef<HTMLDivElement>(null);
  const scrolls = useRef(new Map<string, ImageScroll>());
  const pendingScroll = useRef<{ id: string; scroll: ImageScroll } | null>(null);
  const attemptedRefresh = useRef(new Set<string>());
  const mounted = useRef(true);
  const closing = useRef(false);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<{ kind: 'pan'; x: number; y: number; left: number; top: number }
    | { kind: 'pinch'; distance: number; scale: number; midpoint: { x: number; y: number }; scroll: ImageScroll } | null>(null);
  const image = images[Math.min(index, images.length - 1)];
  const size = image && dimensions[image.id];
  const source = image && (sources[image.id] ?? image.source);
  const mode = image ? views[image.id]?.mode ?? (size ? initialImageFit(size, viewport) : 'fit') : 'fit';
  const scale = size ? mode === 'manual' ? views[image.id].scale : imageFitScale(size, viewport, mode) : 1;
  const placement = size ? imagePlacement(size, viewport, scale) : null;
  const available = Boolean(image && size && loaded[image.id] && !failures[image.id]);
  const canPan = Boolean(placement && (placement.canvasWidth > viewport.width + 1 || placement.canvasHeight > viewport.height + 1));
  const current = useRef({ image, size, scale, viewport, available, mode, canPan });
  current.current = { image, size, scale, viewport, available, mode, canPan };
  const close = useCallback(() => {
    if (closing.current) return;
    closing.current = true;
    dialog.current?.close();
    onClose();
  }, [onClose]);

  useLayoutEffect(() => {
    mounted.current = true;
    const element = dialog.current, previousFocus = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    element?.showModal();
    closeButton.current?.focus({ preventScroll: true });
    return () => {
      mounted.current = false;
      document.body.style.overflow = overflow;
      if (element?.open) element.close();
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, []);

  useLayoutEffect(() => {
    const element = stage.current;
    if (!element) return;
    const measure = () => setViewport({ width: element.clientWidth, height: element.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (!image || !stage.current || !size) return;
    const pending = pendingScroll.current;
    const position = pending?.id === image.id ? pending.scroll : scrolls.current.get(image.id) ?? { left: 0, top: 0 };
    stage.current.scrollLeft = position.left;
    stage.current.scrollTop = position.top;
    scrolls.current.set(image.id, { left: stage.current.scrollLeft, top: stage.current.scrollTop });
    if (pending?.id === image.id) pendingScroll.current = null;
  }, [image?.id, scale, size?.width, size?.height, viewport.width, viewport.height]);

  useEffect(() => {
    const button = thumbStrip.current?.querySelector<HTMLButtonElement>(`[data-image-index="${index}"]`);
    if (button && thumbStrip.current) {
      const strip = thumbStrip.current;
      if (button.offsetLeft < strip.scrollLeft) strip.scrollLeft = button.offsetLeft;
      else if (button.offsetLeft + button.offsetWidth > strip.scrollLeft + strip.clientWidth)
        strip.scrollLeft = button.offsetLeft + button.offsetWidth - strip.clientWidth;
    }
  }, [index, thumbnails]);

  function changeIndex(next: number) {
    if (next < 0 || next >= images.length || next === index) return;
    if (image && stage.current) scrolls.current.set(image.id, { left: stage.current.scrollLeft, top: stage.current.scrollTop });
    pointers.current.clear(); gesture.current = null; setDragging(false);
    setIndex(next);
  }

  const zoom = useCallback((requested: number, point?: { x: number; y: number }, nextMode: ImageFit = 'manual',
    baseline?: { scale: number; scroll: ImageScroll; point: { x: number; y: number } }) => {
    const { image, size, scale, viewport, available } = current.current, element = stage.current;
    if (!image || !size || !element || !available) return;
    const minimum = Math.min(0.05, imageFitScale(size, viewport, 'fit'));
    const next = Math.max(minimum, Math.min(8, requested));
    const anchor = point ?? { x: viewport.width / 2, y: viewport.height / 2 };
    const position = imageZoomScroll(size, viewport, baseline?.scale ?? scale, next,
      baseline?.scroll ?? { left: element.scrollLeft, top: element.scrollTop }, baseline?.point ?? anchor);
    // A moving pinch midpoint pans while preserving its initial image pixel.
    if (baseline) {
      position.left += baseline.point.x - anchor.x;
      position.top += baseline.point.y - anchor.y;
    }
    pendingScroll.current = { id: image.id, scroll: position };
    setViews(previous => ({ ...previous, [image.id]: { mode: nextMode, scale: next } }));
  }, []);

  function fit(nextMode: 'fit' | 'width' | 'actual') {
    if (!image || !size || !available) return;
    if (nextMode === 'actual') { zoom(1, undefined, 'actual'); return; }
    pendingScroll.current = { id: image.id, scroll: { left: 0, top: 0 } };
    setViews(previous => ({ ...previous, [image.id]: { mode: nextMode, scale: imageFitScale(size, viewport, nextMode) } }));
    if (stage.current) { stage.current.scrollLeft = 0; stage.current.scrollTop = 0; }
  }

  useEffect(() => {
    const element = stage.current;
    if (!element) return;
    const wheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      const bounds = element.getBoundingClientRect();
      zoom(current.current.scale * Math.exp(-event.deltaY * 0.008), { x: event.clientX - bounds.left, y: event.clientY - bounds.top });
    };
    element.addEventListener('wheel', wheel, { passive: false });
    return () => element.removeEventListener('wheel', wheel);
  }, [zoom]);

  async function failed(item: LightboxImage) {
    if (!attemptedRefresh.current.has(item.id) && onImageError) {
      attemptedRefresh.current.add(item.id);
      try {
        const replacement = await onImageError(item);
        if (!mounted.current) return;
        if (replacement && replacement !== (sources[item.id] ?? item.source)) {
          setSources(previous => ({ ...previous, [item.id]: replacement }));
          setLoaded(previous => ({ ...previous, [item.id]: false }));
          setFailures(previous => ({ ...previous, [item.id]: false }));
          return;
        }
      } catch { /* The visible failure remains local to this image. */ }
    }
    if (mounted.current) setFailures(previous => ({ ...previous, [item.id]: true }));
  }

  function startGesture() {
    const element = stage.current;
    if (!element) return;
    const points = [...pointers.current.values()];
    if (points.length >= 2) {
      const [a, b] = points, bounds = element.getBoundingClientRect();
      gesture.current = { kind: 'pinch', distance: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)), scale: current.current.scale,
        midpoint: { x: (a.x + b.x) / 2 - bounds.left, y: (a.y + b.y) / 2 - bounds.top },
        scroll: { left: element.scrollLeft, top: element.scrollTop } };
    } else if (points.length === 1) {
      gesture.current = { kind: 'pan', x: points[0].x, y: points[0].y, left: element.scrollLeft, top: element.scrollTop };
    } else gesture.current = null;
  }

  function pointerDown(event: React.PointerEvent<HTMLDivElement>) {
    if (!available || (event.pointerType === 'mouse' && event.button !== 0)) return;
    if ((event.target as Element).closest('button')) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    event.currentTarget.setPointerCapture(event.pointerId);
    startGesture(); setDragging(canPan);
  }

  function pointerMove(event: React.PointerEvent<HTMLDivElement>) {
    if (!pointers.current.has(event.pointerId) || !stage.current) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const state = gesture.current;
    if (state?.kind === 'pan' && pointers.current.size === 1) {
      stage.current.scrollLeft = state.left - (event.clientX - state.x);
      stage.current.scrollTop = state.top - (event.clientY - state.y);
    } else if (state?.kind === 'pinch' && pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()], bounds = stage.current.getBoundingClientRect();
      zoom(state.scale * Math.hypot(a.x - b.x, a.y - b.y) / state.distance,
        { x: (a.x + b.x) / 2 - bounds.left, y: (a.y + b.y) / 2 - bounds.top }, 'manual',
        { scale: state.scale, scroll: state.scroll, point: state.midpoint });
    }
  }

  function pointerUp(event: React.PointerEvent<HTMLDivElement>) {
    pointers.current.delete(event.pointerId);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    startGesture(); if (!pointers.current.size) setDragging(false);
  }

  if (!image) return null;
  const title = image.title?.trim() || `图片 ${index + 1}`;
  return createPortal(<dialog ref={dialog} className="reados-image-lightbox" aria-label="图片预览" data-image-id={image.id}
    onCancel={event => { event.preventDefault(); close(); }}
    onKeyDown={event => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === 'ArrowLeft') { event.preventDefault(); changeIndex(index - 1); }
      else if (event.key === 'ArrowRight') { event.preventDefault(); changeIndex(index + 1); }
      else if (event.key === '+' || event.key === '=') { event.preventDefault(); zoom(scale * 1.25); }
      else if (event.key === '-') { event.preventDefault(); zoom(scale / 1.25); }
      else if (event.key === '0') { event.preventDefault(); fit('fit'); }
    }}>
    <header className="reados-image-lightbox-header">
      <div className="reados-image-lightbox-caption"><span className="reados-image-lightbox-title" title={title}>{title}</span>
        <span className="reados-image-lightbox-count" aria-live="polite">第 {index + 1} / {images.length} 张</span></div>
      <div className="reados-image-lightbox-tools" role="group" aria-label="图片缩放">
        <button type="button" aria-label="缩小" title="缩小（−）" disabled={!available || scale <= Math.min(0.05, size ? imageFitScale(size, viewport, 'fit') : 0.05)} onClick={() => zoom(scale / 1.25)}><Minus size={18}/></button>
        <span className="reados-image-lightbox-scale" aria-label={`缩放比例 ${Math.round(scale * 100)}%`} aria-live="polite">{available ? `${Math.round(scale * 100)}%` : '—'}</span>
        <button type="button" aria-label="放大" title="放大（+）" disabled={!available || scale >= 8} onClick={() => zoom(scale * 1.25)}><Plus size={18}/></button>
        <span className="reados-image-lightbox-divider"/>
        <button type="button" aria-label="适应窗口" aria-pressed={mode === 'fit'} title="适应窗口（0）" disabled={!available} onClick={() => fit('fit')}><Maximize2 size={18}/><span>适应窗口</span></button>
        <button type="button" aria-label="适应宽度" aria-pressed={mode === 'width'} title="适应宽度" disabled={!available} onClick={() => fit('width')}><Expand size={18}/><span>适应宽度</span></button>
        <button type="button" aria-label="原始尺寸（100%）" aria-pressed={mode === 'actual'} title="原始尺寸（100%）" disabled={!available} onClick={() => fit('actual')}>100%</button>
      </div>
      <button ref={closeButton} type="button" className="reados-image-lightbox-close" aria-label="关闭图片预览" title="关闭（Esc）" onClick={close}><X size={21}/></button>
    </header>
    <div ref={stage} className={`reados-image-lightbox-stage${canPan ? ' is-pannable' : ''}${dragging ? ' is-dragging' : ''}`}
      tabIndex={0} aria-label="图片画布，可缩放和拖动" onPointerDown={pointerDown} onPointerMove={pointerMove}
      onPointerUp={pointerUp} onPointerCancel={pointerUp} onLostPointerCapture={pointerUp}
      onScroll={event => scrolls.current.set(image.id, { left: event.currentTarget.scrollLeft, top: event.currentTarget.scrollTop })}
      onDoubleClick={event => {
        if (!available) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        const fitted = imageFitScale(size!, viewport, initialImageFit(size!, viewport));
        if (scale > fitted * 1.2) fit(initialImageFit(size!, viewport) === 'width' ? 'width' : 'fit');
        else zoom(Math.min(8, Math.max(1, scale * 2)), { x: event.clientX - bounds.left, y: event.clientY - bounds.top });
      }}>
      <div className="reados-image-lightbox-canvas" style={{ width: placement?.canvasWidth ?? '100%', height: placement?.canvasHeight ?? '100%' }}>
        {source && <img key={`${image.id}:${source}`} className="reados-image-lightbox-image" src={source} alt={title} draggable={false}
          style={{ width: placement?.width ?? 0, height: placement?.height ?? 0, left: placement?.left ?? 0, top: placement?.top ?? 0, visibility: available ? 'visible' : 'hidden' }}
          onLoad={event => {
            const element = event.currentTarget;
            const naturalSize = { width: element.naturalWidth, height: element.naturalHeight };
            setDimensions(previous => ({ ...previous, [image.id]: naturalSize }));
            setViews(previous => previous[image.id] ? previous : { ...previous,
              [image.id]: { mode: initialImageFit(naturalSize, current.current.viewport), scale: 1 } });
            setLoaded(previous => ({ ...previous, [image.id]: true }));
            setFailures(previous => ({ ...previous, [image.id]: false }));
          }} onError={() => void failed(image)}/>}
      </div>
      {(!source || failures[image.id]) ? <div className="reados-image-lightbox-status" role="status"><span>图片暂时无法加载</span>
        <button type="button" onClick={() => { attemptedRefresh.current.delete(image.id); setFailures(previous => ({ ...previous, [image.id]: false })); void failed(image); }}>重新加载</button></div>
        : !available ? <div className="reados-image-lightbox-status" role="status">正在加载图片…</div> : null}
    </div>
    {images.length > 1 && <><button type="button" className="reados-image-lightbox-prev" aria-label="上一张图片" title="上一张（←）" disabled={index === 0} onClick={() => changeIndex(index - 1)}><ChevronLeft size={24}/></button>
      <button type="button" className="reados-image-lightbox-next" aria-label="下一张图片" title="下一张（→）" disabled={index >= images.length - 1} onClick={() => changeIndex(index + 1)}><ChevronRight size={24}/></button>
      <footer className="reados-image-lightbox-footer"><button type="button" aria-label={thumbnails ? '隐藏缩略图' : '显示缩略图'} aria-expanded={thumbnails} onClick={() => setThumbnails(value => !value)}><GalleryHorizontal size={18}/><span>{thumbnails ? '隐藏缩略图' : '显示缩略图'}</span></button>
        <span>方向键切换 · 双击放大 · Ctrl / ⌘ + 滚轮缩放</span></footer>
      {thumbnails && <div ref={thumbStrip} className="reados-image-lightbox-thumbnails" aria-label="图片缩略图">
        {images.map((item, itemIndex) => <button key={`${item.id}:${itemIndex}`} type="button" data-image-index={itemIndex} aria-label={`查看第 ${itemIndex + 1} 张图片`}
          aria-current={itemIndex === index ? 'true' : undefined} onClick={() => changeIndex(itemIndex)}>
          {(sources[item.id] ?? item.source) ? <img src={sources[item.id] ?? item.source} alt="" loading="lazy" onError={() => void failed(item)}/> : <span>无法加载</span>}<span>{itemIndex + 1}</span></button>)}
      </div>}</>}
  </dialog>, document.body);
}
